import {
  EXTENSION_MASK_PLACEHOLDER,
  EXTENSION_TEXT_PLACEHOLDER,
  PHONE_MASK_PLACEHOLDER,
  stripMaskPlaceholder,
} from "../constants";
import { formatPhoneOrDigits } from "../fields/PhoneNumberInput";
import type { CallScriptVerifiedFields } from "./callScript.types";
import { mergeOptionalNumber, mergeOptionalString, mergeString, toYesNo } from "./callScriptShared.payload";
import type { CallScriptApplicantRole, CallScriptSaveWarning } from "./callScriptShared.types";

// ---------------------------------------------------------------------------
// callScript.savePayload (OR-21767)
//
// Builds the POST body for WorkbenchIncomeVerificationApi.saveCallScript.
// Besides the original top-level call-outcome fields, the body now also
// carries the employment data itself: the dashboard's saved Employment
// record (see callScriptEmploymentMapping) merged with whatever the analyst
// entered live on the call — a non-blank Verified Information value wins,
// otherwise the saved value is sent, so an unrelated field the analyst never
// touched isn't blanked out server-side. A field with neither a Verified nor
// a saved value is left `undefined`, so it's omitted from the JSON body
// entirely rather than sent as an empty string or `null`. Field names
// (`employer*`/`workLocation*` prefixes) come from a real sample payload,
// not a generated client type — recap-service's saveCallScript is typed
// `object` on the generated client (no request-body schema).
// ---------------------------------------------------------------------------

/** Request body for the Call Script save (POST) endpoint. */
export interface CallScriptSaveBody {
  applicantRole: CallScriptApplicantRole;
  incomeSlot: number;
  applicantEmploymentPk?: number;
  applicantAddressPk?: number;
  phoneNumberDialed: string;
  verificationOutcome: string;
  isVerificationOutsourced: "Y" | "N";
  isPhoneNoBad: "Y" | "N";
  hireDate: string;
  noteText: string;
  employerName?: string;
  employerPhone?: string;
  /** No corresponding "Verified Information" field in the modal — always the saved Employment record's value. */
  employerPhoneExtension?: string;
  employerStreetNumber?: string;
  employerStreetName?: string;
  employerApartmentNumber?: string;
  employerCity?: string;
  employerState?: string;
  employerZip?: string;
  occupation?: string;
  employmentTypeId?: number;
  /**
   * Not present in the confirmed sample payload — no swagger/contract exists
   * for this endpoint to verify these names against. Backend/QA should
   * confirm before relying on them.
   */
  payFrequency?: string;
  grossPayPerPeriod?: number;
  avgHoursPerWeek?: number;
  dateOfTermination?: string;
  verifiedWith?: string;
  /** Physical Work Location fields. */
  workLocationName?: string;
  workLocationStreetNumber?: string;
  workLocationStreetName?: string;
  workLocationApartmentNumber?: string;
  workLocationCity?: string;
  workLocationState?: string;
  workLocationZip?: string;
  workLocationPhone?: string;
  /** Per-question verification answers. */
  verificationAnswers: CallScriptVerificationAnswer[];
}

/** One question's verified-vs-stored answer comparison. */
export interface CallScriptVerificationAnswer {
  verificationQuestionPk: number;
  answerEntered: string;
  answerStored: string;
  isAnswerMatch: "Y" | "N";
}

/**
 * Response body for the Call Script save (POST) endpoint. Deliberately thin —
 * just the persisted identifiers, not a full history entry — so callers must
 * refetch history to render the saved call. `warnings` is populated when the
 * call itself saved but part of the merged employment data didn't (e.g. an
 * invalid phone number) — the save still succeeds (2xx), so these surface as
 * warnings in the modal rather than blocking the save.
 */
export interface CallScriptSaveResponse {
  verificationPhoneCallPk: number;
  verificationAnswerPks: number[];
  dpscNotePk: number;
  dpscNoteType: string;
  occurredAt: string;
  warnings?: CallScriptSaveWarning[];
}

/** Call outcomes computed from the verified fields — see {@link computeVerificationOutcome}. */
export const VERIFICATION_OUTCOME = {
  RPC: "RPC - Right Party contacted",
  NOT_FUNDABLE: "Not Fundable",
  NO_RPC_NO_ISSUES: "No RPC - No Issues",
  NO_ATTEMPT: "No Attempt",
} as const;

/**
 * Derives the call outcome from the verified fields: no number dialed means
 * no attempt was made; a hire date means the right party was reached; absent
 * a hire date, a confirmed-bad original number means the lead isn't
 * fundable, otherwise the call reached the right party with no issues found.
 * @param {CallScriptVerifiedFields} verifiedFields - The modal's "Verified Information" column.
 * @returns {string} One of the {@link VERIFICATION_OUTCOME} values.
 */
export function computeVerificationOutcome(verifiedFields: CallScriptVerifiedFields): string {
  const phoneDialed = verifiedFields.phoneNumberDialed.trim();
  const hireDate = verifiedFields.hireDate.trim();
  if (!phoneDialed) {
    return VERIFICATION_OUTCOME.NO_ATTEMPT;
  }
  if (hireDate) {
    return VERIFICATION_OUTCOME.RPC;
  }
  return verifiedFields.originalNumberBad ? VERIFICATION_OUTCOME.NOT_FUNDABLE : VERIFICATION_OUTCOME.NO_RPC_NO_ISSUES;
}

type VerifiedFieldValue = CallScriptVerifiedFields[keyof CallScriptVerifiedFields];

/** Fields compared/sent as NANP `xxx-xxx-xxxx`, not raw digits. */
const PHONE_ANSWER_FIELDS = new Set<keyof CallScriptVerifiedFields>([
  "phoneNumberDialed",
  "employerPhone",
  "workLocationPhone",
]);

/**
 * Stringifies a verified-field value for the save contract's "Y"/"N" flags,
 * formatting phone fields (see {@link PHONE_ANSWER_FIELDS}) to `xxx-xxx-xxxx`
 * so the entered/stored comparison isn't tripped up by formatting differences.
 * Non-canonical digit counts (e.g. a legacy saved phone with fewer than 10
 * digits) are left as bare digits rather than truncated/reformatted — see
 * {@link formatPhoneOrDigits} — so two genuinely different numbers can't be
 * truncated into a false match.
 * @param {keyof CallScriptVerifiedFields} field - Which verified field this value belongs to.
 * @param {VerifiedFieldValue} value - The raw field value.
 * @returns {string} The stringified answer.
 */
function toAnswerString(field: keyof CallScriptVerifiedFields, value: VerifiedFieldValue): string {
  if (typeof value === "boolean") {
    return toYesNo(value);
  }
  const stringValue = String(value);
  return PHONE_ANSWER_FIELDS.has(field) ? formatPhoneOrDigits(stringValue) : stringValue;
}

/**
 * Whether a verified-field value counts as unanswered. Booleans are never blank.
 * @param {VerifiedFieldValue} value - The raw field value.
 * @returns {boolean} Whether the value is blank.
 */
function isBlank(value: VerifiedFieldValue): boolean {
  return typeof value !== "boolean" && String(value).trim() === "";
}

/**
 * Builds one question's verified-vs-saved answer comparison, or `undefined`
 * when the field wasn't entered or has no matching question pk — no answer
 * row is emitted in either case.
 * @param {keyof CallScriptVerifiedFields} field - Which verified field to compare.
 * @param {CallScriptVerifiedFields} verified - The modal's "Verified Information" column.
 * @param {CallScriptVerifiedFields} saved - The baseline to compare against (employment + prior history).
 * @param {Partial<Record<keyof CallScriptVerifiedFields, number>>} pkMap - Field -> verificationQuestionPk.
 * @returns {CallScriptVerificationAnswer | undefined} The answer row, if any.
 */
function toAnswer(
  field: keyof CallScriptVerifiedFields,
  verified: CallScriptVerifiedFields,
  saved: CallScriptVerifiedFields,
  pkMap: Partial<Record<keyof CallScriptVerifiedFields, number>>
): CallScriptVerificationAnswer | undefined {
  const verificationQuestionPk = pkMap[field];
  const enteredValue = verified[field];
  if (verificationQuestionPk === undefined || isBlank(enteredValue)) {
    return undefined;
  }
  const answerEntered = toAnswerString(field, enteredValue);
  const storedValue = saved[field];
  const answerStored = toAnswerString(field, storedValue);
  const isAnswerMatch = !isBlank(storedValue) && answerEntered.toLowerCase() === answerStored.toLowerCase();
  return { verificationQuestionPk, answerEntered, answerStored, isAnswerMatch: toYesNo(isAnswerMatch) };
}

/**
 * Builds the `verificationAnswers` array by comparing every field with a
 * resolved question pk against its saved baseline.
 * @param {CallScriptVerifiedFields} verified - The modal's "Verified Information" column.
 * @param {CallScriptVerifiedFields} saved - The baseline to compare against (employment + prior history).
 * @param {Partial<Record<keyof CallScriptVerifiedFields, number>>} pkMap - Field -> verificationQuestionPk.
 * @returns {CallScriptVerificationAnswer[]} The answer rows for the save request.
 */
export function buildVerificationAnswers(
  verified: CallScriptVerifiedFields,
  saved: CallScriptVerifiedFields,
  pkMap: Partial<Record<keyof CallScriptVerifiedFields, number>>
): CallScriptVerificationAnswer[] {
  const answers: CallScriptVerificationAnswer[] = [];
  for (const field of Object.keys(pkMap) as Array<keyof CallScriptVerifiedFields>) {
    const answer = toAnswer(field, verified, saved, pkMap);
    if (answer) {
      answers.push(answer);
    }
  }
  return answers;
}

/** Everything the modal knows that feeds the save body. */
export interface CallScriptSaveContext {
  /** Which person's income this call is for — maps to `applicantRole`. */
  personPath: "driver" | "secondSigner";
  /** Zero-based index of the income block within that person's incomes; sent as the 1-based `incomeSlot`. */
  incomeIndex: number;
  /** Primary key of the dashboard Employment record being verified. */
  applicantEmploymentPk?: string | number;
  /** Primary key of the applicant's physical residence address. */
  applicantAddressPk?: string | number;
  /** Free-text verification note typed in the modal. */
  note: string;
  /** The modal's "Verified Information" column. */
  verifiedFields: CallScriptVerifiedFields;
  /** Baseline used to detect answer matches — employment + prior history for fields with no Employment backing. */
  savedFieldsForAnswers: CallScriptVerifiedFields;
  /** Field -> verificationQuestionPk, resolved from the fetched question catalog. */
  questionPkMap: Partial<Record<keyof CallScriptVerifiedFields, number>>;
  /** No corresponding "Verified Information" field in the modal — passed through as-is from the saved Employment record. */
  employerPhoneExtension: string | undefined;
}

/**
 * Coerces a BFF identifier (exposed as a string on the generated models) to the
 * numeric primary key the save contract expects. Non-numeric / missing values
 * are dropped rather than sent as `NaN`.
 * @param {string | number | undefined} value - The raw identifier.
 * @returns {number | undefined} The numeric primary key, when parseable.
 */
function toPrimaryKey(value: string | number | undefined): number | undefined {
  if (value === undefined || value === "") {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/**
 * Same as {@link mergeOptionalString}, additionally formatting phone fields
 * to NANP `xxx-xxx-xxxx` when there are exactly 10 digits — see
 * {@link formatPhoneOrDigits} for why a non-canonical digit count (e.g. a
 * legacy saved phone) is sent as bare digits instead of a fake-looking
 * formatted string.
 * @param {string} verifiedValue - The analyst-entered value.
 * @param {string} savedValue - The saved-employment baseline.
 * @returns {string | undefined} The merged phone, or undefined to omit the field.
 */
function mergeOptionalPhone(verifiedValue: string, savedValue: string): string | undefined {
  const merged = stripMaskPlaceholder(mergeString(verifiedValue, savedValue), PHONE_MASK_PLACEHOLDER);
  return merged.replaceAll(/\D/gu, "") === "" ? undefined : formatPhoneOrDigits(merged);
}

/**
 * Builds the Call Script save request body: the original call-outcome fields,
 * plus the merged employment data (Verified Information overlaid onto the
 * saved Employment record — see the module comment).
 * @param {CallScriptSaveContext} context - The modal's save context.
 * @returns {CallScriptSaveBody} The request body.
 */
export function buildCallScriptSaveBody(context: CallScriptSaveContext): CallScriptSaveBody {
  const {
    personPath,
    incomeIndex,
    applicantEmploymentPk,
    applicantAddressPk,
    note,
    verifiedFields: verified,
    savedFieldsForAnswers: saved,
    questionPkMap,
    employerPhoneExtension,
  } = context;

  const employmentTypeCode = mergeString(verified.employmentType, saved.employmentType);

  return {
    applicantRole: personPath === "driver" ? "DRIVER" : "SECOND_SIGNER",
    incomeSlot: incomeIndex + 1,
    applicantEmploymentPk: toPrimaryKey(applicantEmploymentPk),
    applicantAddressPk: toPrimaryKey(applicantAddressPk),
    phoneNumberDialed: formatPhoneOrDigits(verified.phoneNumberDialed),
    verificationOutcome: computeVerificationOutcome(verified),
    isVerificationOutsourced: toYesNo(verified.verificationOutsourced),
    isPhoneNoBad: toYesNo(verified.originalNumberBad),
    hireDate: verified.hireDate,
    noteText: note,
    employerName: mergeOptionalString(verified.employerName, saved.employerName),
    employerPhone: mergeOptionalPhone(verified.employerPhone, saved.employerPhone),
    employerPhoneExtension:
      stripMaskPlaceholder(
        employerPhoneExtension?.trim() ?? "",
        EXTENSION_MASK_PLACEHOLDER,
        EXTENSION_TEXT_PLACEHOLDER
      ) || undefined,
    employerStreetNumber: mergeOptionalString(verified.streetNumber, saved.streetNumber),
    employerStreetName: mergeOptionalString(verified.streetName, saved.streetName),
    employerApartmentNumber: mergeOptionalString(verified.apartmentNumber, saved.apartmentNumber),
    employerCity: mergeOptionalString(verified.city, saved.city),
    employerState: mergeOptionalString(verified.state, saved.state),
    employerZip: mergeOptionalString(verified.zip, saved.zip),
    occupation: mergeOptionalString(verified.occupation, saved.occupation),
    employmentTypeId: employmentTypeCode === "" ? undefined : Number(employmentTypeCode),
    payFrequency: mergeOptionalString(verified.payFrequency, saved.payFrequency),
    grossPayPerPeriod: mergeOptionalNumber(verified.grossPayPerPeriod, saved.grossPayPerPeriod),
    avgHoursPerWeek: mergeOptionalNumber(verified.avgHoursPerWeek, saved.avgHoursPerWeek),
    dateOfTermination: mergeOptionalString(verified.dateOfTermination, saved.dateOfTermination),
    verifiedWith: mergeOptionalString(verified.verifiedWith, saved.verifiedWith),
    workLocationName: mergeOptionalString(verified.workLocationName, saved.workLocationName),
    workLocationStreetNumber: mergeOptionalString(verified.workLocationStreetNumber, saved.workLocationStreetNumber),
    workLocationStreetName: mergeOptionalString(verified.workLocationStreetName, saved.workLocationStreetName),
    workLocationApartmentNumber: mergeOptionalString(
      verified.workLocationApartmentNumber,
      saved.workLocationApartmentNumber
    ),
    workLocationCity: mergeOptionalString(verified.workLocationCity, saved.workLocationCity),
    workLocationState: mergeOptionalString(verified.workLocationState, saved.workLocationState),
    workLocationZip: mergeOptionalString(verified.workLocationZip, saved.workLocationZip),
    workLocationPhone: mergeOptionalPhone(verified.workLocationPhone, saved.workLocationPhone),
    verificationAnswers: buildVerificationAnswers(verified, saved, questionPkMap),
  };
}

export { type CallScriptApplicantRole, type CallScriptSaveWarning } from "./callScriptShared.types";
